import type { Field, FieldComponent } from '@atlas/react-forms';
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Errors from '../../Errors';
import Select from '../Select';

import styles from './DateSelectField.module.scss';

const months: Field['options'] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
].map((item, index) => {
  return {
    text: item,
    value: `${index + 1}`,
  };
});

const datesInMonth = (year: number, month: number): number => {
  const daysInMonth: { [key: number]: number } = {
    1: 31,
    2: 28,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };

  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

  if (month === 2) {
    return isLeapYear ? 29 : 28;
  }

  return daysInMonth[month];
};

const currentDate = new Date();

const MIN_YEAR = 1927;

const yearOptions = Array.from(
  { length: currentDate.getFullYear() - MIN_YEAR + 1 },
  (_, i) => MIN_YEAR + i,
).map((year) => ({
  text: String(year),
  value: String(year),
}));

const DateSelectField: FieldComponent = ({
  field,
  onFocus,
  onBlur,
  onChange,
}) => {
  const [selectedDate, setSelectedDate] = useState<{
    day: number;
    month: number;
    year: number;
  }>(() => {
    const date = field.value ? new Date(+field.value) : new Date();

    return {
      day: date.getUTCDate(),
      month: date.getUTCMonth() + 1,
      year: date.getFullYear(),
    };
  });

  const prevSelectedDate = useRef(selectedDate);

  useEffect(() => {
    if (
      prevSelectedDate.current.day !== selectedDate.day ||
      prevSelectedDate.current.month !== selectedDate.month ||
      prevSelectedDate.current.year !== selectedDate.year
    ) {
      onChange?.(
        // selectedDate.month - 1 . UTC date indexing 0 - 11
        Date.UTC(selectedDate.year, selectedDate.month - 1, selectedDate.day),
        false,
      );
      prevSelectedDate.current = selectedDate;
    }
  }, [selectedDate, onChange]);

  const handleDayChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedDate((prevState) => ({
      ...prevState,
      day: Number(e.target.value),
    }));
  };

  const handleMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedMonth = Number(e.target.value);

    setSelectedDate((prevState) => ({ ...prevState, month: selectedMonth }));
  };

  const handleYearChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedYear = Number(e.target.value);

    setSelectedDate((prevState) => ({ ...prevState, year: selectedYear }));
  };

  const createDaysOptions = useCallback((month: number, year: number) => {
    const daysInSelectedMonth = datesInMonth(year, month);

    return Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1).map(
      (day) => ({
        text: String(day),
        value: String(day),
      }),
    );
  }, []);

  return (
    <div className={styles['form-field-container']}>
      <div className={styles['date-select-field']}>
        <Select
          data-testid="day-select"
          options={createDaysOptions(selectedDate.month, selectedDate.year)}
          value={String(selectedDate.day)}
          onFocus={onFocus}
          disabled={field.disabled}
          onBlur={onBlur}
          isValid={!field.errors.length && field.touched}
          onChange={handleDayChange}
        />
        <Select
          data-testid="month-select"
          options={months}
          value={String(selectedDate.month)}
          onFocus={onFocus}
          disabled={field.disabled}
          onBlur={onBlur}
          isValid={!field.errors.length && field.touched}
          onChange={handleMonthChange}
        />
        <Select
          data-testid="year-select"
          options={yearOptions}
          value={String(selectedDate.year)}
          onFocus={onFocus}
          disabled={field.disabled}
          onBlur={onBlur}
          isValid={!field.errors.length && field.touched}
          onChange={handleYearChange}
        />
      </div>
      {field.errors.length > 0 && field.touched && (
        <Errors errors={field.errors} />
      )}
    </div>
  );
};

export default DateSelectField;
